import React, { useState, useEffect } from "react";
import {
  Box, Card, Container, Typography, Grid, Switch, FormControlLabel,
  TextField, Button, Divider, Alert, Stack, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Slider,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { doc, onSnapshot, setDoc, serverTimestamp, addDoc, collection } from "firebase/firestore";
import { db } from "../database/db";
import Page from "../components/Page";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DEFAULT_SETTINGS = {
  maintenanceMode: false,
  maintenanceMessage: "Fire Investing is undergoing scheduled maintenance. Back shortly!",
  minIosVersion: "1.0.0",
  minAndroidVersion: "1.0.0",
  forceUpdate: false,
  fivMonitorEnabled: true,
  fivThreshold: 75,
  communityFeedEnabled: true,
  livestreamEnabled: true,
  tokenSystemEnabled: true,
  aiEnabled: true,
  maxTokenPurchasePerDay: 10000,
  referralBonusTokens: 50,
  newUserBonusTokens: 100,
  supportEmail: "support@fireinvesting.com",
  appVersion: "1.0.0",
};

function SettingRow({ label, description, children, danger = false }) {
  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: danger ? "error.main" : "text.primary" }}>
            {label}
          </Typography>
          {description && (
            <Typography variant="caption" color="text.secondary">{description}</Typography>
          )}
        </Box>
        <Box sx={{ flexShrink: 0 }}>{children}</Box>
      </Box>
    </Box>
  );
}

export default function AppSettings() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmMaintenance, setConfirmMaintenance] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "AppSettings", "appConfig"), (snap) => {
      if (snap.exists()) {
        setSettings({ ...DEFAULT_SETTINGS, ...snap.data() });
      }
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const update = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "AppSettings", "appConfig"), {
        ...settings,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      await addDoc(collection(db, "AuditLog"), {
        action: "settings-update",
        targetName: "AppSettings/appConfig",
        details: { keys: Object.keys(settings) },
        timestamp: serverTimestamp(),
      });
      toast.success("Settings saved");
      setDirty(false);
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
    setSaving(false);
  };

  const handleMaintenanceToggle = (val) => {
    if (val) {
      setConfirmMaintenance(true);
    } else {
      update("maintenanceMode", false);
    }
  };

  if (loading) {
    return (
      <Page title="App Settings | Fire Investing Admin">
        <Container><Box sx={{ display: "flex", justifyContent: "center", pt: 10 }}><CircularProgress sx={{ color: "#3dc10e" }} /></Box></Container>
      </Page>
    );
  }

  return (
    <Page title="App Settings | Fire Investing Admin">
      <ToastContainer position="top-right" />
      <Container maxWidth="lg">
        <Box sx={{ pb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>App Settings</Typography>
            <Typography variant="body2" color="text.secondary">Global feature flags, version control, and app configuration</Typography>
          </Box>
          <Button
            variant="contained"
            disabled={!dirty || saving}
            onClick={handleSave}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Icon icon="eva:save-fill" />}
            sx={{ bgcolor: "#3dc10e", "&:hover": { bgcolor: "#097936" } }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>

        {dirty && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            You have unsaved changes. Click "Save Changes" to apply them to the live app.
          </Alert>
        )}

        {settings.maintenanceMode && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <strong>MAINTENANCE MODE IS ON</strong> — The app is currently showing a maintenance screen to all users.
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Danger Zone */}
          <Grid item xs={12}>
            <Card sx={{ p: 3, borderRadius: 3, border: "1px solid rgba(255,72,66,0.3)", boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "error.main", mb: 1 }}>
                🚨 Danger Zone
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <SettingRow label="Maintenance Mode" description="Takes the entire app offline for all users. Shows maintenance message." danger>
                <Switch checked={settings.maintenanceMode} onChange={(e) => handleMaintenanceToggle(e.target.checked)}
                  color="error" />
              </SettingRow>
              {settings.maintenanceMode && (
                <TextField fullWidth multiline rows={2} label="Maintenance Message"
                  value={settings.maintenanceMessage}
                  onChange={(e) => update("maintenanceMessage", e.target.value)}
                  sx={{ mt: 1 }} />
              )}
              <Divider />
              <SettingRow label="Force App Update" description="Forces all users to update before using the app." danger>
                <Switch checked={settings.forceUpdate} onChange={(e) => update("forceUpdate", e.target.checked)} color="error" />
              </SettingRow>
            </Card>
          </Grid>

          {/* Version Control */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Version Control</Typography>
              <Divider sx={{ mb: 1 }} />
              <Stack spacing={2}>
                <TextField size="small" fullWidth label="Min iOS Version (e.g. 1.2.0)"
                  value={settings.minIosVersion}
                  onChange={(e) => update("minIosVersion", e.target.value)} />
                <TextField size="small" fullWidth label="Min Android Version (e.g. 1.2.0)"
                  value={settings.minAndroidVersion}
                  onChange={(e) => update("minAndroidVersion", e.target.value)} />
                <TextField size="small" fullWidth label="Current App Version"
                  value={settings.appVersion}
                  onChange={(e) => update("appVersion", e.target.value)} />
              </Stack>
            </Card>
          </Grid>

          {/* Feature Flags */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Feature Flags</Typography>
              <Divider sx={{ mb: 1 }} />
              {[
                ["communityFeedEnabled", "Community Feed", "Show/hide the StockTwits-style feed"],
                ["livestreamEnabled", "Live Streaming", "Enable/disable live streams"],
                ["tokenSystemEnabled", "Token System", "Enable/disable token purchases and gifts"],
                ["aiEnabled", "AI Assistant", "Enable/disable the GPT AI features"],
                ["fivMonitorEnabled", "FIV Monitor", "Enable/disable FIV threshold alerts"],
              ].map(([key, label, desc]) => (
                <React.Fragment key={key}>
                  <SettingRow label={label} description={desc}>
                    <Switch checked={!!settings[key]} onChange={(e) => update(key, e.target.checked)} color="primary" />
                  </SettingRow>
                  <Divider />
                </React.Fragment>
              ))}
            </Card>
          </Grid>

          {/* FIV Settings */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>FIV Monitor Settings</Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="caption" color="text.secondary">Alert Threshold (%)</Typography>
              <Box sx={{ px: 1 }}>
                <Slider
                  value={settings.fivThreshold || 75}
                  onChange={(_, v) => update("fivThreshold", v)}
                  min={50} max={95} step={5}
                  marks={[{ value: 75, label: "75%" }]}
                  valueLabelDisplay="on"
                  sx={{ color: "#3dc10e" }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Stocks where community holds ≥{settings.fivThreshold}% will trigger alerts
              </Typography>
            </Card>
          </Grid>

          {/* Token Settings */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Token Settings</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <TextField size="small" fullWidth label="New User Bonus (tokens)"
                  type="number" value={settings.newUserBonusTokens}
                  onChange={(e) => update("newUserBonusTokens", parseInt(e.target.value))} />
                <TextField size="small" fullWidth label="Referral Bonus (tokens)"
                  type="number" value={settings.referralBonusTokens}
                  onChange={(e) => update("referralBonusTokens", parseInt(e.target.value))} />
                <TextField size="small" fullWidth label="Max Daily Token Purchase"
                  type="number" value={settings.maxTokenPurchasePerDay}
                  onChange={(e) => update("maxTokenPurchasePerDay", parseInt(e.target.value))} />
                <TextField size="small" fullWidth label="Support Email"
                  value={settings.supportEmail}
                  onChange={(e) => update("supportEmail", e.target.value)} />
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Confirm Maintenance Mode */}
      <Dialog open={confirmMaintenance} onClose={() => setConfirmMaintenance(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Enable Maintenance Mode?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will immediately take the app offline for <strong>all users</strong>. They will see your maintenance message until you disable this.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmMaintenance(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => { update("maintenanceMode", true); setConfirmMaintenance(false); }}>
            Enable Maintenance
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
